// Copyright (c) 2026 Goffy. All Rights Reserved. This file belongs to Goffy.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { Redirect, useLocalSearchParams, useRouter, useRootNavigationState } from 'expo-router';

import { Text, View } from '@/components/Themed';
import { t } from '@/i18n';
import {
  getSharedBffWebSocket,
  type TourFreezeLockRejectedEvent,
  type TourFreezeLockedEvent,
  type TourLeaderboardStateEvent,
  type TourLeaderboardUpdateEvent,
} from '@/services/bffWebSocket';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import HolePointsGrid, { resolveHoleGridColumns } from '@/features/tour-games/components/HolePointsGrid';
import type { HoleGridCell } from '@/features/tour-games/components/HolePointsGrid';
import { computeSelectionTotals } from '@/features/tour-games/freeze';
import { normalizeFreezeSection } from '@/features/tour-games/normalize';
import type { FreezeHoleCell, FreezePlayerStanding, FreezeSection } from '@/features/tour-games/types';

type PendingLock = {
  requestId: string;
  playerId: string;
  holeNumbers: number[];
};

const LOCK_TIMEOUT_MS = 12000;

// The BFF deserializes requestId as a Guid, so this must be a real UUID —
// same generator as roundstart.tsx.
function generateRequestId(): string {
  const uuid = (globalThis.crypto as { randomUUID?: () => string } | undefined)?.randomUUID?.();
  if (uuid) return uuid;
  const segment = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(1);
  return `${segment()}${segment()}-${segment()}-4${segment().slice(1)}-a${segment().slice(1)}-${segment()}${segment()}${segment()}`;
}

// Post-round Freeze screen: lock in Stableford points on chosen holes from the
// just-finished round. Anyone in the round can lock for any player whose lock
// window is open (same trust model as score entry). Locked holes are permanent.
export default function FreezeLockScreen() {
  const { roundId: roundIdParam, tourId: tourIdParam } = useLocalSearchParams<{
    roundId?: string;
    tourId?: string;
  }>();
  const router = useRouter();
  const { width: viewportWidth } = useWindowDimensions();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const roundId = useMemo(() => {
    const raw = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return raw ?? null;
  }, [roundIdParam]);
  const [tourId, setTourId] = useState<string | null>(() => {
    const raw = Array.isArray(tourIdParam) ? tourIdParam[0] : tourIdParam;
    return raw ?? null;
  });

  const [freezeSection, setFreezeSection] = useState<FreezeSection | null>(null);
  const [playerNames, setPlayerNames] = useState<Map<string, string>>(new Map());
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [loggedInPlayerId, setLoggedInPlayerId] = useState<string | null>(null);
  const manualSelectionRef = useRef(false);
  const [selectedHoles, setSelectedHoles] = useState<Set<number>>(new Set());
  const [includeEarlier, setIncludeEarlier] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSwitchPlayerId, setPendingSwitchPlayerId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const pendingLockRef = useRef<PendingLock | null>(null);
  const lockTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resolve the tour id from the round when opened without ?tourId=.
  useEffect(() => {
    if (tourId || !roundId || !isNavigationReady) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : undefined;
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, { headers });
        if (!response.ok) return;
        const data = (await response.json()) as { tourId?: string; TourId?: string };
        const resolved = data.tourId ?? data.TourId ?? null;
        if (!cancelled && resolved) setTourId(resolved);
      } catch {
        // The snapshot request below will surface the error state.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isNavigationReady, roundId, tourId, user?.token]);

  useEffect(() => {
    if (!isNavigationReady || !tourId) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    socket.setSubscriptionContext({ roundId, tourId });

    const applyFreeze = (raw: unknown) => {
      const section = normalizeFreezeSection(raw);
      if (section) {
        setFreezeSection(section);
        setLoading(false);
        setError(null);
      }
    };

    const unsubscribe = socket.onMessage((message) => {
      if (message.type === 'tour.leaderboard.state') {
        const state = message as TourLeaderboardStateEvent;
        if (state.tourId && tourId && state.tourId.toLowerCase() !== tourId.toLowerCase()) return;
        const names = new Map<string, string>();
        (state.players ?? []).forEach((player) => {
          const id = (player.id ?? player.Id) as string | undefined;
          const name = (player.name ?? player.Name) as string | undefined;
          if (id && name) names.set(id, name);
        });
        if (names.size > 0) setPlayerNames(names);
        applyFreeze(state.freeze);
        setLoading(false);
      } else if (message.type === 'tour.leaderboard.update') {
        const update = message as TourLeaderboardUpdateEvent;
        if (update.tourId && tourId && update.tourId.toLowerCase() !== tourId.toLowerCase()) return;
        applyFreeze(update.freeze);
      } else if (message.type === 'tour.freeze.locked') {
        const locked = message as TourFreezeLockedEvent;
        const pending = pendingLockRef.current;
        if (pending && locked.requestId === pending.requestId) {
          pendingLockRef.current = null;
          if (lockTimeoutRef.current) clearTimeout(lockTimeoutRef.current);
          setSaving(false);
          setSelectedHoles(new Set());
          setStatusNote(t('freeze.lockSaved', { count: pending.holeNumbers.length }));
        }
      } else if (message.type === 'tour.freeze.lock.rejected') {
        const rejected = message as TourFreezeLockRejectedEvent;
        const pending = pendingLockRef.current;
        if (pending && rejected.requestId === pending.requestId) {
          pendingLockRef.current = null;
          if (lockTimeoutRef.current) clearTimeout(lockTimeoutRef.current);
          setSaving(false);
          setError(rejected.message || t('freeze.lockRejected'));
        }
      }
    });

    socket.sendTourLeaderboardRequest({ tourId, requestId: generateRequestId() });

    return () => {
      unsubscribe();
      if (lockTimeoutRef.current) clearTimeout(lockTimeoutRef.current);
    };
  }, [apiBaseUrl, isNavigationReady, roundId, tourId, user?.token]);

  // Resolve the logged-in user's player id so their own chip gets preselected.
  useEffect(() => {
    if (!user?.id || !user.token) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const payload = (await response.json()) as { id?: string };
        if (!cancelled && payload.id) setLoggedInPlayerId(payload.id);
      } catch {
        // Fall back to the first-player default selection.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, user?.id, user?.token]);

  // Players lockable from THIS round first, then the rest of the field.
  const standings = useMemo(() => {
    if (!freezeSection) return [] as FreezePlayerStanding[];
    return [...freezeSection.standings].sort((a, b) => {
      const aHere = a.window.isOpen && a.window.sourceRoundId === roundId ? 0 : 1;
      const bHere = b.window.isOpen && b.window.sourceRoundId === roundId ? 0 : 1;
      return aHere - bHere;
    });
  }, [freezeSection, roundId]);

  // Preselect the logged-in player once their id resolves; until then default to
  // the first standing. Never override a manual pick or in-progress hole selection.
  useEffect(() => {
    if (standings.length === 0 || manualSelectionRef.current) return;
    const own = loggedInPlayerId
      ? standings.find((standing) => standing.playerId.toLowerCase() === loggedInPlayerId.toLowerCase())
      : undefined;
    if (own) {
      if (own.playerId !== selectedPlayerId && selectedHoles.size === 0) {
        setSelectedPlayerId(own.playerId);
      }
      return;
    }
    if (!selectedPlayerId) setSelectedPlayerId(standings[0].playerId);
  }, [loggedInPlayerId, selectedHoles, selectedPlayerId, standings]);

  if (!user) {
    return <Redirect href="/" />;
  }

  if (!roundId) {
    return (
      <View style={styles.errorPage}>
        <Text style={styles.errorText}>{t('freeze.loadError')}</Text>
      </View>
    );
  }

  const selectedStanding = standings.find((standing) => standing.playerId === selectedPlayerId) ?? null;
  const windowOpenHere =
    !!selectedStanding &&
    selectedStanding.window.isOpen &&
    selectedStanding.window.sourceRoundId === roundId;

  // Opened-up mode: this round is finished but is not the player's lock-window
  // round — a checkbox lets the user lock this round's holes anyway.
  const roundFinished =
    (freezeSection?.rounds.find((round) => round.roundId.toLowerCase() === roundId.toLowerCase())?.status ?? '')
      .toLowerCase() === 'finished';
  const holePointsHere = (hole: FreezeHoleCell): number | null =>
    hole.pointsByRound.find((entry) => entry.roundId.toLowerCase() === roundId.toLowerCase())?.points ?? null;
  const canOpenEarlier =
    !!selectedStanding &&
    !windowOpenHere &&
    roundFinished &&
    !freezeSection?.isFinal &&
    selectedStanding.holes.some((hole) => !hole.locked && holePointsHere(hole) !== null);
  const earlierActive = canOpenEarlier && includeEarlier;

  // In opened-up mode the grid shows THIS round's points on unlocked holes.
  const effectiveHoles: FreezeHoleCell[] = (selectedStanding?.holes ?? []).map((hole) => {
    if (!earlierActive || hole.locked) return hole;
    const points = holePointsHere(hole);
    return { ...hole, points, lockable: points !== null };
  });
  const lockingHere = windowOpenHere || earlierActive;
  const totals = selectedStanding
    ? computeSelectionTotals(effectiveHoles, selectedHoles)
    : {
        lockedTotal: 0,
        lockedCount: 0,
        selectedCount: 0,
        selectedPoints: 0,
        projectedLockedTotal: 0,
        projectedAverage: null,
      };
  const lockableRemaining = effectiveHoles.filter((hole) => hole.lockable).length;

  const switchPlayer = (playerId: string) => {
    manualSelectionRef.current = true;
    setSelectedPlayerId(playerId);
    setSelectedHoles(new Set());
    setIncludeEarlier(false);
    setStatusNote(null);
    setPendingSwitchPlayerId(null);
  };

  // Switching player discards picked-but-unlocked holes, so ask first.
  const requestPlayerSwitch = (playerId: string) => {
    if (playerId === selectedPlayerId) return;
    if (totals.selectedCount > 0) {
      setPendingSwitchPlayerId(playerId);
      return;
    }
    switchPlayer(playerId);
  };

  const toggleHole = (holeNumber: number) => {
    setStatusNote(null);
    setSelectedHoles((prev) => {
      const next = new Set(prev);
      if (next.has(holeNumber)) {
        next.delete(holeNumber);
      } else {
        next.add(holeNumber);
      }
      return next;
    });
  };

  const submitLocks = () => {
    if (!selectedStanding || !tourId || totals.selectedCount === 0 || saving) return;
    const holeNumbers = Array.from(selectedHoles).sort((a, b) => a - b);
    const requestId = generateRequestId();
    pendingLockRef.current = { requestId, playerId: selectedStanding.playerId, holeNumbers };
    setSaving(true);
    setError(null);
    const socket = getSharedBffWebSocket(apiBaseUrl);
    // A 'queued' outcome is not a failure — the frame is flushed when the socket (re)opens, so
    // the lock is still in flight. The timeout below is the only thing that gives up.
    socket.sendTourFreezeLock({
      tourId,
      playerId: selectedStanding.playerId,
      roundId,
      holeNumbers,
      requestId,
      allowEarlierRounds: earlierActive || undefined,
    });
    lockTimeoutRef.current = setTimeout(() => {
      if (pendingLockRef.current?.requestId === requestId) {
        pendingLockRef.current = null;
        setSaving(false);
        setError(t('freeze.lockTimeout'));
      }
    }, LOCK_TIMEOUT_MS);
    setConfirmOpen(false);
  };

  // Picked from the width left inside the page (20px) and card (14px) padding.
  const gridWidth = viewportWidth - 68;
  const gridColumns = resolveHoleGridColumns(gridWidth);

  const cells: HoleGridCell[] = effectiveHoles.map((hole) => {
    if (hole.locked) {
      return { holeNumber: hole.holeNumber, points: hole.points, state: 'locked' as const };
    }
    if (lockingHere && hole.lockable) {
      return {
        holeNumber: hole.holeNumber,
        points: hole.points,
        state: selectedHoles.has(hole.holeNumber) ? ('selected' as const) : ('selectable' as const),
      };
    }
    return {
      holeNumber: hole.holeNumber,
      points: hole.points,
      state: hole.points === null ? ('disabled' as const) : ('plain' as const),
    };
  });

  const selectedName = selectedStanding
    ? playerNames.get(selectedStanding.playerId) ?? t('tourLeaderboard.unknownPlayer')
    : '';

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>{t('freeze.title')}</Text>
      <Text style={styles.subtitle}>{t('freeze.subtitle')}</Text>
      {loading ? <Text style={styles.meta}>{t('freeze.loading')}</Text> : null}
      {error ? <Text style={styles.errorText} testID="freeze-error">{error}</Text> : null}
      {statusNote ? <Text style={styles.successText} testID="freeze-status">{statusNote}</Text> : null}
      {!loading && standings.length === 0 ? <Text style={styles.meta}>{t('freeze.noPlayers')}</Text> : null}

      {standings.length > 0 ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.playerChips}>
          {standings.map((standing) => {
            const selected = standing.playerId === selectedPlayerId;
            const openHere = standing.window.isOpen && standing.window.sourceRoundId === roundId;
            return (
              <Pressable
                key={standing.playerId}
                style={[styles.playerChip, selected && styles.playerChipSelected, !openHere && styles.playerChipClosed]}
                onPress={() => requestPlayerSwitch(standing.playerId)}
                testID={`freeze-player-${standing.playerId}`}
              >
                <Text style={[styles.playerChipText, selected && styles.playerChipTextSelected]} numberOfLines={1}>
                  {playerNames.get(standing.playerId) ?? t('tourLeaderboard.unknownPlayer')}
                </Text>
                <Text style={[styles.playerChipMeta, selected && styles.playerChipTextSelected]}>
                  {t('freeze.chipLockedCount', { locked: standing.lockedHoleCount })}
                  {standing.lockedHoleCount > 0
                    ? ` · ${t('freeze.lockedAvg', {
                        avg: (standing.lockedPoints / standing.lockedHoleCount).toFixed(1),
                      })}`
                    : ''}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>
      ) : null}

      {selectedStanding ? (
        <View style={styles.card}>
          {!lockingHere ? (
            <Text style={styles.meta} testID="freeze-window-closed">
              {selectedStanding.window.isOpen ? t('freeze.windowOtherRound') : t('freeze.windowClosed')}
              {canOpenEarlier ? ` ${t('freeze.openEarlierHint')}` : ''}
            </Text>
          ) : lockableRemaining === 0 ? (
            <Text style={styles.meta}>{t('freeze.allLocked')}</Text>
          ) : (
            <Text style={styles.meta}>{t('freeze.selectHint', { name: selectedName })}</Text>
          )}
          {canOpenEarlier ? (
            <Pressable
              style={styles.checkboxRow}
              onPress={() => {
                setIncludeEarlier((prev) => !prev);
                setSelectedHoles(new Set());
                setStatusNote(null);
              }}
              testID="freeze-open-earlier-checkbox"
            >
              <View style={[styles.checkboxBox, includeEarlier && styles.checkboxBoxChecked]}>
                {includeEarlier ? <Text style={styles.checkboxMark}>✓</Text> : null}
              </View>
              <Text style={styles.checkboxLabel}>{t('freeze.openEarlierLabel')}</Text>
            </Pressable>
          ) : null}
          <HolePointsGrid
            cells={cells}
            onPressHole={lockingHere ? toggleHole : undefined}
            testIDPrefix="freeze-grid"
            columns={gridColumns}
          />
          <View style={styles.totalsRow}>
            <View style={styles.totalsItem}>
              <Text style={styles.totalsLabel}>{t('freeze.lockedTotalLabel')}</Text>
              <Text style={[styles.totalsValue, styles.totalsLocked]}>{totals.lockedTotal}</Text>
            </View>
            <View style={styles.totalsItem}>
              <Text style={styles.totalsLabel}>{t('freeze.selectedLabel')}</Text>
              <Text style={[styles.totalsValue, styles.totalsSelected]}>+{totals.selectedPoints}</Text>
            </View>
            <View style={styles.totalsItem}>
              <Text style={styles.totalsLabel}>{t('freeze.afterLockLabel')}</Text>
              <Text style={styles.totalsValue}>{totals.projectedLockedTotal}</Text>
            </View>
            <View style={styles.totalsItem}>
              <Text style={styles.totalsLabel}>{t('freeze.avgLabel')}</Text>
              <Text style={styles.totalsValue} testID="freeze-avg">
                {totals.projectedAverage === null ? '–' : totals.projectedAverage.toFixed(1)}
              </Text>
            </View>
          </View>
          {lockingHere ? (
            <Pressable
              style={[styles.lockButton, (totals.selectedCount === 0 || saving) && styles.lockButtonDisabled]}
              disabled={totals.selectedCount === 0 || saving}
              onPress={() => setConfirmOpen(true)}
              testID="freeze-lock-button"
            >
              <Text style={styles.lockButtonText}>
                {saving ? t('freeze.saving') : t('freeze.lockButton', { count: totals.selectedCount })}
              </Text>
            </Pressable>
          ) : null}
          <Text style={styles.permanentNote}>{t('freeze.permanentNote')}</Text>
        </View>
      ) : null}

      <Pressable style={styles.backButton} onPress={() => router.back()}>
        <Text style={styles.backButtonText}>{t('freeze.done')}</Text>
      </Pressable>

      <Modal visible={confirmOpen} transparent animationType="fade" onRequestClose={() => setConfirmOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setConfirmOpen(false)}>
          <Pressable style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('freeze.confirmTitle')}</Text>
            <Text style={styles.modalBody}>
              {t('freeze.confirmMessage', { count: totals.selectedCount, name: selectedName })}
            </Text>
            <Pressable style={styles.lockButton} onPress={submitLocks} testID="freeze-confirm-button">
              <Text style={styles.lockButtonText}>{t('freeze.confirmCta')}</Text>
            </Pressable>
            <Pressable style={styles.cancelButton} onPress={() => setConfirmOpen(false)}>
              <Text style={styles.cancelButtonText}>{t('freeze.cancel')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={pendingSwitchPlayerId !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setPendingSwitchPlayerId(null)}
      >
        <Pressable style={styles.modalBackdrop} onPress={() => setPendingSwitchPlayerId(null)}>
          <Pressable style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t('freeze.switchTitle')}</Text>
            <Text style={styles.modalBody}>
              {t('freeze.switchMessage', { count: totals.selectedCount, name: selectedName })}
            </Text>
            <Pressable
              style={styles.lockButton}
              onPress={() => {
                if (pendingSwitchPlayerId) switchPlayer(pendingSwitchPlayerId);
              }}
              testID="freeze-switch-confirm"
            >
              <Text style={styles.lockButtonText}>{t('freeze.switchCta')}</Text>
            </Pressable>
            <Pressable
              style={styles.cancelButton}
              onPress={() => setPendingSwitchPlayerId(null)}
              testID="freeze-switch-cancel"
            >
              <Text style={styles.cancelButtonText}>{t('freeze.switchStay')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#011806',
  },
  content: {
    padding: 20,
    gap: 14,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  subtitle: {
    fontSize: 14,
    color: appColors.text.secondary,
  },
  meta: {
    color: appColors.text.primary,
    fontSize: 13,
  },
  errorText: {
    color: '#ff9a9a',
    fontSize: 13,
  },
  successText: {
    color: appColors.accent.primary,
    fontSize: 13,
  },
  playerChips: {
    gap: 8,
    paddingVertical: 2,
  },
  playerChip: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    alignItems: 'center',
    maxWidth: 160,
  },
  playerChipSelected: {
    backgroundColor: appColors.accent.primary,
    borderColor: appColors.accent.primary,
  },
  playerChipClosed: {
    opacity: 0.55,
  },
  playerChipText: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  playerChipTextSelected: {
    color: appColors.text.inverse,
  },
  playerChipMeta: {
    fontSize: 11,
    color: appColors.text.muted,
  },
  card: {
    backgroundColor: 'rgba(15,40,16,0.78)',
    borderRadius: 16,
    padding: 14,
    gap: 12,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  checkboxBox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: '#A1D493',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  checkboxBoxChecked: {
    backgroundColor: '#A1D493',
  },
  checkboxMark: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.inverse,
  },
  checkboxLabel: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: appColors.surface.elevated,
    borderRadius: 12,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  totalsItem: {
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  totalsLabel: {
    fontSize: 11,
    color: appColors.text.secondary,
  },
  totalsValue: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  totalsLocked: {
    color: appColors.accent.gold,
  },
  totalsSelected: {
    color: appColors.accent.primary,
  },
  lockButton: {
    paddingVertical: 14,
    borderRadius: 20,
    backgroundColor: '#A1D493',
    alignItems: 'center',
  },
  lockButtonDisabled: {
    opacity: 0.6,
  },
  lockButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.inverse,
  },
  permanentNote: {
    fontSize: 11,
    color: appColors.text.muted,
    textAlign: 'center',
  },
  backButton: {
    paddingVertical: 14,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#A1D493',
    alignItems: 'center',
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalCard: {
    width: '100%',
    backgroundColor: '#011806',
    borderRadius: 20,
    padding: 16,
    gap: 12,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  modalBody: {
    fontSize: 14,
    color: appColors.text.secondary,
  },
  cancelButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    color: appColors.text.secondary,
  },
  errorPage: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: '#011806',
  },
});
